import { useCallback, useState } from "react";
import Filter from "../components/UniversalBenchmark/Filter";
import { useDropzone } from "react-dropzone";
import ExcelJS from "exceljs";
import DropzoneComponent from "../components/Utils/DropzoneComponent";
import OverlayLoading from "../components/OverlayLoading";
import { toast } from "react-toastify";
import BenchmarkCard from "../components/UniversalBenchmark/BenchmarkCard";
import { colors } from "../global";
import Title from "../components/title";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { apiFetch } from "../apiClient";

const UniversalBenchmark = () => {
  const [sourceType, setSourceType] = useState("csv");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [benchmarkList, setBenchmarkList] = useState({});
  const [checkedCards, setCheckedCards] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const handleCheckChange = (key) => {
    setCheckedCards((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  const onDrop = useCallback(
    async (acceptedFiles) => {
      setIsSubmitting(true);

      const convertFile = async (file) => {
        if (!file) return;

        try {
          const formData = new FormData();
          formData.append("file", file);

          const response = await apiFetch(`/api/v1/universalBenchmark/upload`, {
            method: "POST",
            body: formData,
            credentials: "include",
          });

          if (!response.ok) {
            let errorMessage = "Upload failed.";
            try {
              const errorResponse = await response.json();
              if (errorResponse.detail) {
                errorMessage = errorResponse.detail;
              } else if (errorResponse.error) {
                errorMessage = errorResponse.error;
              }
            } catch (parseError) {
              console.error("Failed to parse error response", parseError);
            }

            toast.error(errorMessage);
            return;
          } else {
            const result = await response.json();
            setBenchmarkList(result.data);
            const allChecked = Object.keys(result.data).reduce((acc, key) => {
              acc[key] = true;
              return acc;
            }, {});
            setCheckedCards(allChecked);
          }
        } catch (err) {
          if (!err.message.includes("Upload failed.")) {
            toast.error("Upload failed. Please try again.");
          }
        }
      };

      if (acceptedFiles.length === 0) {
        setIsSubmitting(false);
        return;
      }

      const file = acceptedFiles[0];
      const originalName = file.name;
      const fileExtension = originalName.split(".").pop();

      if (fileExtension === "xlsx") {
        try {
          const buffer = await file.arrayBuffer();
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);
        } catch (error) {
          console.error("Error reading Excel file:", error);
        }
      }

      await convertFile(file);
      setIsSubmitting(false);
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: false,
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const selectedData = Object.entries(checkedCards)
        .filter(([key, isChecked]) => isChecked)
        .reduce((acc, [key]) => {
          if (benchmarkList[key]) {
            acc[key] = benchmarkList[key];
          }
          return acc;
        }, {});

      const response = await apiFetch(`/api/v1/universalBenchmark/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ data: selectedData }),
        credentials: "include",
      });

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          } else if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        } catch (parseError) {
          console.error("Failed to parse error response", parseError);
        }

        toast.error(errorMessage);
        return;
      }
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "_")
        .split(".")[0];

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Universal_Benchmark_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      if (!err.message.includes("Upload failed.")) {
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnselectAll = () => {
    const newChecked = Object.keys(benchmarkList).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});
    setCheckedCards(newChecked);
  };

  const handleSelectAll = () => {
    const newChecked = Object.keys(benchmarkList).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setCheckedCards(newChecked);
  };

  const anySelected = Object.values(checkedCards).some((val) => val);
  const isSelectAll = !anySelected;
  const handleClick = isSelectAll ? handleSelectAll : handleUnselectAll;
  const buttonText = isSelectAll ? "Select All" : "Unselect All";
  const iconPath = isSelectAll ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12";

  return (
    <div>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}
      <div className=" p-6 pb-0">
        <Title title={"Universal Benchmark"} />
      </div>
      <div className="m-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="mb-4 flex gap-6">
          <label
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setSourceType("csv")}
          >
            <div className="relative">
              <input
                type="radio"
                name="source"
                value="csv"
                checked={sourceType === "csv"}
                onChange={() => setSourceType("csv")}
                className="sr-only"
              />
              <div
                className={`w-5 h-5 rounded-full border-2 transition-all duration-200 ease-in-out flex items-center justify-center ${
                  sourceType === "csv"
                    ? "bg-primary border-primary"
                    : "bg-white border-gray-300 hover:border-primary"
                }`}
                style={{
                  backgroundColor:
                    sourceType === "csv" ? colors.primary : "white",
                  borderColor:
                    sourceType === "csv" ? colors.primary : "#d1d5db",
                }}
              >
                {sourceType === "csv" && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
            </div>
            <span className="text-gray-700 font-medium">Upload CSV</span>
          </label>

          <label
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => setSourceType("db")}
          >
            <div className="relative">
              <input
                type="radio"
                name="source"
                value="db"
                checked={sourceType === "db"}
                onChange={() => setSourceType("db")}
                className="sr-only"
              />
              <div
                className={`w-5 h-5 rounded-full border-2 transition-all duration-200 ease-in-out flex items-center justify-center ${
                  sourceType === "db"
                    ? "bg-primary border-primary"
                    : "bg-white border-gray-300 hover:border-primary"
                }`}
                style={{
                  backgroundColor:
                    sourceType === "db" ? colors.primary : "white",
                  borderColor: sourceType === "db" ? colors.primary : "#d1d5db",
                }}
              >
                {sourceType === "db" && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
            </div>
            <span className="text-gray-700 font-medium">
              Collect from Database
            </span>
          </label>
        </div>

        {sourceType === "csv" ? (
          <DropzoneComponent
            getInputProps={getInputProps}
            getRootProps={getRootProps}
            isDragActive={isDragActive}
            fileTypes="CSV"
          />
        ) : (
          <Filter />
        )}
      </div>
      <div className="space-y-6 p-4 pb-4">
        {Object.entries(benchmarkList).length > 0 ? (
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search benchmark name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg bg-white focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 focus:outline-none transition-all duration-200 text-gray-700 placeholder-gray-400"
                style={{
                  borderColor: searchTerm ? colors.primary : "#e5e7eb",
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            <button
              onClick={handleClick}
              className="px-4 py-2 bg-white border-2 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all duration-200 font-medium flex items-center gap-2 whitespace-nowrap"
              style={{
                borderColor: colors.accent,
                color: colors.accent,
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.accent;
                e.target.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
                e.target.style.color = colors.accent;
              }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={iconPath}
                />
              </svg>
              {buttonText}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 space-y-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div
              className="flex items-center justify-center w-16 h-16 rounded-full"
              style={{ backgroundColor: colors.gray }}
            >
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-2xl"
              />
            </div>
            <div className="text-center">
              <p
                className="text-lg font-medium"
                style={{ color: colors.primary }}
              >
                No data available
              </p>
              <p className="text-sm mt-1" style={{ color: colors.darkGray }}>
                The file appears to be empty or could not be processed
              </p>
            </div>
          </div>
        )}

        {Object.entries(benchmarkList)
          .filter(([key]) =>
            key.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map(([key, array]) => (
            <BenchmarkCard
              key={key}
              benchmarkName={key}
              data={array}
              isCheck={!!checkedCards[key]}
              onCheckChange={() => handleCheckChange(key)}
            />
          ))}
        {Object.values(checkedCards).filter(Boolean).length > 0 && (
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                Object.values(checkedCards).filter(Boolean).length === 0
              }
              className="py-2 px-6 rounded-md font-medium shadow-sm transition-all flex items-center justify-center hover:cursor-pointer"
              style={{
                backgroundColor: isSubmitting ? "#ccc" : colors.accent,
                color: colors.white,
                opacity:
                  isSubmitting ||
                  Object.values(checkedCards).filter(Boolean).length === 0
                    ? 0.7
                    : 1,
              }}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : (
                "Download File"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversalBenchmark;
